import { Component } from '@theme/component';
import { oncePerEditorSession } from '@theme/utilities';

/**
 * Comparison slider component for comparing two images
 *
 * @typedef {object} ComparisonSliderRefs
 * @property {HTMLElement} mediaWrapper - The container for the images
 * @property {HTMLInputElement} slider - The range input element
 * @property {HTMLElement} afterImage - The image that gets revealed
 *
 * @extends {Component<ComparisonSliderRefs>}
 *
 * @property {string[]} requiredRefs - Required refs: 'mediaWrapper', 'slider', and 'afterImage'
 */
export class ComparisonSliderComponent extends Component {
  requiredRefs = ['mediaWrapper', 'slider', 'afterImage'];

  constructor() {
    super();
    this.hasAnimated = false;
    this.boundHandleIntersection = this.handleIntersection.bind(this);
  }

  /**
   * Called when component is added to DOM
   */
  connectedCallback() {
    super.connectedCallback();

    const { mediaWrapper } = this.refs;

    // Get orientation from media wrapper
    this.orientation = mediaWrapper.dataset.orientation || 'horizontal';

    // Initialize the position
    this.sync();

    // Enable pointer-based dragging (works on touch; the native range only jumps on tap)
    this.setupPointerDrag();

    // Set up intersection observer for animation
    this.setupIntersectionObserver();
  }

  /**
   * Drag the divider with pointer events so the handle follows the finger/mouse.
   * The native range input only responds to a tap on touch devices (it jumps but
   * does not drag), so we drive --compare directly from the pointer position.
   */
  setupPointerDrag() {
    const { mediaWrapper, slider } = this.refs;
    if (!mediaWrapper || !slider) return;

    const updateFromEvent = (event) => {
      const rect = mediaWrapper.getBoundingClientRect();
      let compare;
      if (this.orientation === 'vertical') {
        const fraction = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
        compare = Math.round(100 - fraction * 100);
      } else {
        // Computed from the physical left edge, so the divider follows the finger in RTL too.
        const fraction = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
        compare = Math.round(fraction * 100);
      }
      this.isAnimating = false;
      mediaWrapper.style.setProperty('--transition-duration', '0s');
      mediaWrapper.style.setProperty('--compare', String(compare));
      slider.value = String(compare);
    };

    let dragging = false;

    mediaWrapper.addEventListener('pointerdown', (event) => {
      dragging = true;
      this.hasAnimated = true; // user interacted — skip the intro hint animation
      try {
        mediaWrapper.setPointerCapture(event.pointerId);
      } catch (e) {
        /* setPointerCapture can throw if the pointer is already gone */
      }
      updateFromEvent(event);
    });

    mediaWrapper.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      updateFromEvent(event);
    });

    const stop = (event) => {
      if (!dragging) return;
      dragging = false;
      try {
        mediaWrapper.releasePointerCapture(event.pointerId);
      } catch (e) {
        /* ignore */
      }
    };

    mediaWrapper.addEventListener('pointerup', stop);
    mediaWrapper.addEventListener('pointercancel', stop);

    // Belt-and-suspenders: block native image/element drag so dragging the divider on desktop
    // never starts an image ghost-drag.
    mediaWrapper.addEventListener('dragstart', (event) => event.preventDefault());
  }

  /**
   * Sync the CSS custom property with the input value
   */
  sync() {
    const { mediaWrapper, slider } = this.refs;
    // Skip sync during animation to prevent lag
    if (this.isAnimating) return;

    const val = (Number(slider.value) - Number(slider.min)) / (Number(slider.max) - Number(slider.min));
    const compareValue = Math.round(val * 100);

    // Set the CSS custom property on the media wrapper
    mediaWrapper.style.setProperty('--compare', String(compareValue));
  }

  /**
   * Clean up when component is removed
   */
  disconnectedCallback() {
    // Clean up intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  /**
   * Set the slider value and update display
   * @param {number} value - Value between 0-100 (0 = all after, 100 = all before)
   */
  setValue(value) {
    const { slider } = this.refs;
    if (!slider) return;

    slider.value = String(value);
    this.sync();
  }

  /**
   * Animate the slider handle to give users a hint about the interaction
   */
  animateSlider() {
    const { mediaWrapper, slider } = this.refs;
    if (this.hasAnimated) return;

    this.hasAnimated = true;
    this.isAnimating = true;

    // Enable transition for smooth animation
    mediaWrapper.style.setProperty('--transition-duration', '0.5s');

    // Create a subtle sliding animation by only setting CSS property
    setTimeout(() => {
      mediaWrapper.style.setProperty('--compare', '40');
    }, 100);

    setTimeout(() => {
      mediaWrapper.style.setProperty('--compare', '60');
    }, 600);

    setTimeout(() => {
      mediaWrapper.style.setProperty('--compare', '50');
    }, 1100);

    setTimeout(() => {
      // Remove transition after animation and sync slider value
      mediaWrapper.style.setProperty('--transition-duration', '0s');
      // Sync the slider value to match the final position
      slider.value = '50';
      this.isAnimating = false;
    }, 1600);
  }

  /**
   * Set up intersection observer to detect when section comes into view
   */
  setupIntersectionObserver() {
    if (!window.IntersectionObserver) return;

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5, // Trigger when 50% of the component is visible
    };

    this.intersectionObserver = new IntersectionObserver(this.boundHandleIntersection, options);
    this.intersectionObserver.observe(this);
  }

  /**
   * Handle intersection observer callback
   * @param {IntersectionObserverEntry[]} entries
   */
  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !this.hasAnimated) {
        // Add a small delay to ensure everything is rendered
        setTimeout(() => {
          oncePerEditorSession(this, `comparison-slider-animated`, () => {
            this.animateSlider();
          });
        }, 300);

        // Disconnect observer after first animation
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }
      }
    });
  }
}

// Register the custom element
if (!customElements.get('comparison-slider-component')) {
  customElements.define('comparison-slider-component', ComparisonSliderComponent);
}
